<template>
  <ValidationObserver ref="validator" slim>
    <div class="row">
      <div class="col-xs-12 col-sm-6 col-md-12">
        <div class="lib__text-center">
          <h3>{{ $t('appointment.workFileNumber') }}: {{ searchAvailabilitiesParameters.workFileReference }}</h3>
        </div>
      </div>
    </div>
    <FormMain :title="title" data-cy="appointment-component">
      <template>
        <Feedback v-if="isAppointmentNotAvailable" level="danger" icon simple>
          <p>{{ $t('appointment.appointmentNotAvailable') }}</p>
        </Feedback>
        <p v-html="$t('appointment.description')" v-if="hasSlots"></p>
        <!-- search for availabilities/wait -->
        <SearchAvailabilities v-if="currentSearchAvailabilitiesParameters"
          :createAppointmentStatus="createAppointmentStatus" :parameters="currentSearchAvailabilitiesParameters"
          :waitingLabel="$t('workFileTracking.appointment.waiting')" @loaded="onSlotsLoaded" @loading="onSlotsLoading"
          @availabilitiesError="availabilitiesError" />
        <template>
          <AppointmentSlots :loadingSlots="slotsLoading" :currentSlots="currentSlots" required
            v-model="appointment.selectedSlot" @delete="onDelete" :hasSlotsSearchError="hasSlotsSearchError"
            @input="onSelectedSlotChanged" />
          <AppointmentTimeslotSelection :currentSlots="currentTimeSlots"
            v-if="appointment.selectedSlot && displayTimelotSelection" @input="onTimeSlotSelect"
            @cancel="onTimeSlotCancel" @remove="onDateRemove" />
        </template>
      </template>
      <Feedback level="alert" icon simple>
        <p>{{ $t('appointment.remarksMessage') }}</p>
      </Feedback>
      <TextZone :label="$t('appointment.remarks')" v-model="appointment.remarks" v-if="isDateSlotSelected"
        :placeholder="$t('appointment.remarksPlaceholder')">
        <template v-slot:note>
          {{ $t('appointment.remarksNote') }}
        </template>
      </TextZone>
    </FormMain>
  </ValidationObserver>
</template>

<script lang="ts">
import { Vue, Component, Watch, Prop } from 'vue-property-decorator'
import { helper } from '@/services'
import {
  AppointmentInfo,
  AppointmentResponse,
  AppointmentSlot,
  CreateAppointmentStatus,
  GetAppointmentAvailabilitiesRequest,
  appointmentSearchInterval
} from '@/models'
import AppointmentSlots from '@/components/inputs/Appointment/AppointmentSlots.vue'
import AppointmentTimeslotSelection from '@/components/inputs/Appointment/AppointmentTimeslotSelection.vue'
import SearchAvailabilities from '@/components/inputs/Appointment/SearchAvailabilities.vue'
import moment from 'moment'
import { Mutation } from 'vuex-class'

@Component({
  name: 'Appointment',
  components: {
    AppointmentSlots,
    AppointmentTimeslotSelection,
    SearchAvailabilities
  }
})
export default class Appointment extends Vue {
  //#region [Props]
  // Parameters for availabilities initial search
  @Prop({ type: Object, required: true })
  public searchAvailabilitiesParameters!: GetAppointmentAvailabilitiesRequest
  // Limit for availabilities incremental search
  @Prop({ type: Date, required: true }) public searchEndDate!: Date
  // Status of the appointment creation
  @Prop({ type: String, default: null })
  public createAppointmentStatus!: CreateAppointmentStatus | null
  //#endregion

  //#region [Data]
  public appointment: AppointmentInfo = {
    remarks: null,
    selectedSlot: null,
    noSatisfyingDate: false
  }

  public clickedDateSlot: AppointmentSlot | null = null
  public currentSlots: AppointmentSlot[] | null = null
  public currentSearchStartDate: Date | null = null
  public currentSearchEndDate: Date | null = null
  public seeMoreDates: boolean = true
  public seeMoreDatesLoading: boolean = false
  public displayTimelotSelection: boolean = false

  private hasSlotsSearchError: boolean = false
  // Current search parameters
  private currentSearchAvailabilitiesParameters: GetAppointmentAvailabilitiesRequest | null = null

  private slotsLoading: boolean = false
  //#endregion

  private get currentTimeSlots(): AppointmentSlot[] | null {
    if (this.currentSlots && this.appointment.selectedSlot) {
      const key = moment(this.appointment.selectedSlot.startDate).format("YYYY-MM-DD")
      return this.currentSlots.filter(i => moment(i.startDate).format("YYYY-MM-DD") === key)
    }

    return null;
  }

  private get hasSlots(): boolean {
    return !!this.currentSlots && this.currentSlots.length > 0
  }

  private get isAppointmentNotAvailable(): boolean {
    return this.createAppointmentStatus === CreateAppointmentStatus.AppointmentNotAvailable
  }

  private get otherSlot(): AppointmentSlot {
    return {
      startDate: null,
      endDate: null,
      timeslotLabel: '',
      key: 'other-date',
      otherDate: true
    }
  }

  private get isDateSlotSelected(): boolean {
    return !!this.appointment.selectedSlot && this.appointment.selectedSlot.key !== 'other-date'
  }

  private get title(): string {
    return this.hasSlotsSearchError ? '' : this.$t('appointment.title').toString()
  }

  //#endregion

  //#region [Mutations]
  @Mutation('CLEAR_API_ERRORS', { namespace: 'apiErrors' })
  private clearApiErrors!: () => void
  //#endregion

  //#region [Watch]
  @Watch('slots', { immediate: true, deep: true })
  public onSlotsChange(value: AppointmentSlot[] | null) {
    if (!value) {
      return
    }
    this.currentSlots = helper.clone(value)
  }

  @Watch('appointment', { deep: true })
  public onAppointmentChanged(value: AppointmentInfo) {
    if (value) {
      this.$emit('input', value)
    }
  }
  //#endregion

  //#region [Method]

  public async mounted() {
    // Set the current search dates
    this.currentSearchStartDate = this.searchAvailabilitiesParameters.periodStartDate
    this.currentSearchEndDate = moment(this.currentSearchStartDate).add(appointmentSearchInterval, 'days').toDate()
    // Trigger the search for availabilities
    this.currentSearchAvailabilitiesParameters = this.getComputedSearchAvailabilitiesParameters(false)
  }

  public availabilitiesError() {
    this.$emit('availabilitiesError')
  }

  public onSelectedSlotChanged(value: AppointmentSlot) {
    if (value) {
      if (value.otherDate) {
        this.appointment.selectedSlot = this.otherSlot
        this.appointment.noSatisfyingDate = true
      } else {
        this.clickedDateSlot = value
        this.displayTimelotSelection = true
      }
    }

  }

  public onTimeSlotSelect(value: AppointmentSlot) {
    this.displayTimelotSelection = false
    if (!value) {
      return
    }
    this.appointment.selectedSlot!.timeslotLabel = value.timeslotLabel
    this.appointment.selectedSlot!.endDate = value.endDate
    this.appointment.selectedSlot!.startDate = value.startDate
    this.appointment.selectedSlot!.otherDate = value.otherDate

    this.appointment.noSatisfyingDate = value.otherDate
  }

  public onTimeSlotCancel() {
    this.displayTimelotSelection = false
    // No timeslot chosen
    // Remove selection and clear validation errors
    this.removeSelectedSlotAndClearValidationErrors()
  }

  public onDelete() {
    // Remove selection and clear validation errors
    this.removeSelectedSlotAndClearValidationErrors()
  }

  public onDateRemove(date: Date) {
    this.displayTimelotSelection = false
    // No availability anymore, remove this date
    const slotToRemove = this.currentSlots!.find((s) => s.startDate === date)
    this.currentSlots!.splice(this.currentSlots!.indexOf(slotToRemove!), 1)
    // Remove selection and clear errors
    this.removeSelectedSlotAndClearValidationErrors()
  }

  public removeSelectedSlotAndClearValidationErrors() {
    this.clearValidationErrors()
    this.appointment.selectedSlot = null
  }

  public clearValidationErrors() {
    const validator: any = this.$refs.validator
    validator.reset()
  }

  private seeOtherDates() {
    this.seeMoreDatesLoading = true

    this.clearValidationErrors()
    this.clearApiErrors()
    // Increment the search dates
    this.currentSearchStartDate = moment(this.currentSlots![this.currentSlots!.length - 1].startDate!).add(1, 'days').toDate()
    this.currentSearchEndDate = moment(this.currentSearchStartDate!).add(7, 'days').toDate()

    const endOfSearchPeriodReached = this.currentSearchEndDate! >= this.searchEndDate
    this.seeMoreDates = !endOfSearchPeriodReached

    // Trigger the search for availabilities
    this.currentSearchAvailabilitiesParameters = this.getComputedSearchAvailabilitiesParameters(true)
  }

  private onSlotsLoaded(value: AppointmentResponse | null) {
    this.clearValidationErrors()
    this.seeMoreDatesLoading = false
    this.hasSlotsSearchError = !value
    if (value) {
      this.currentSlots = value.slots
    }
  }

  private onSlotsLoading(value: boolean) {
    this.slotsLoading = value
  }

  private getComputedSearchAvailabilitiesParameters(
    seeOtherDatesClicked: boolean
  ): GetAppointmentAvailabilitiesRequest {
    return {
      workFileReference: this.searchAvailabilitiesParameters.workFileReference,
      topic: this.searchAvailabilitiesParameters.topic,
      service: this.searchAvailabilitiesParameters.service,
      periodStartDate: this.currentSearchStartDate!,
      periodEndDate: this.currentSearchEndDate!,
      workRequestType: this.searchAvailabilitiesParameters.workRequestType,
      powerLevel: this.searchAvailabilitiesParameters.powerLevel,
      seeOtherDatesClicked
    }
  }

  //#endregion

  //#region [Emit]
  //#endregion
}
</script>